"use client";
import { DailyAvailabilityByUnitId } from "@/types/DailyAvailabilitiesByUnitId";
import { Unit } from "@/types/Unit";
import { keys } from "lodash";
import { DateTime } from "luxon";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import "./index.scss";

const WEEKDAY_TO_CLASSNAME = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const HotelAvailabilityCalendarClient = ({
  startDate,
  availabilityCalendarStartDate,
  availabilityCalendarEndDate,
  units,
  dailyAvailabilitiesPerUnitId,
  hotelShortName,
}: {
  startDate: string;
  availabilityCalendarStartDate: string;
  availabilityCalendarEndDate: string;
  units: Unit[];
  dailyAvailabilitiesPerUnitId: DailyAvailabilityByUnitId;
  hotelShortName: string;
}) => {
  const [visibleStartDate, setVisibleStartDate] = useState(startDate);

  const windowRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const numberOfDaysToScroll = DateTime.fromISO(startDate).diff(
      DateTime.fromISO(availabilityCalendarStartDate),
      "days"
    ).days;

    setVisibleStartDate(availabilityCalendarStartDate);
    windowRef.current &&
      windowRef.current.scrollTo &&
      windowRef.current.scrollTo(numberOfDaysToScroll * 36, 0);
  }, [startDate, availabilityCalendarStartDate, availabilityCalendarEndDate]);

  const availabilityCalendarStartDateLuxon = DateTime.fromISO(visibleStartDate);
  const availabilityCalendarEndDateLuxon = DateTime.fromISO(
    availabilityCalendarEndDate
  );

  const renderMonthHeaderNodes = () => {
    const monthHeaderNodes = [];
    let currentDateLuxon = availabilityCalendarStartDateLuxon;
    while (currentDateLuxon <= availabilityCalendarEndDateLuxon) {
      let numberOfDaysInMonth = currentDateLuxon.daysInMonth;
      if (currentDateLuxon.equals(availabilityCalendarStartDateLuxon)) {
        numberOfDaysInMonth = numberOfDaysInMonth! - currentDateLuxon.day + 1;
      }

      const monthLabel = currentDateLuxon.toLocaleString({
        month: "long",
      });
      const yearLabel = currentDateLuxon.toLocaleString({
        year: "numeric",
      });

      monthHeaderNodes.push(
        <th
          className="month-header"
          colSpan={numberOfDaysInMonth}
          scope="colgroup"
          key={`${monthLabel}-${yearLabel}`}
        >
          <div className="date">
            <span className="month">{monthLabel}</span>{" "}
            <span className="year">{yearLabel}</span>
          </div>
        </th>
      );

      currentDateLuxon = currentDateLuxon.plus({ months: 1 }).startOf("month");
    }

    return monthHeaderNodes;
  };

  const renderDateHeaderNodes = () => {
    const dateHeaderNodes = [];
    let currentDateLuxon = availabilityCalendarStartDateLuxon;
    while (currentDateLuxon <= availabilityCalendarEndDateLuxon) {
      const dayOfWeek = currentDateLuxon.toLocaleString({ weekday: "short" });
      const day = currentDateLuxon.day;

      const dayOfWeekClassName = WEEKDAY_TO_CLASSNAME[currentDateLuxon.weekday];

      dateHeaderNodes.push(
        <th
          className={`date-header ${dayOfWeekClassName}`}
          key={currentDateLuxon.toISODate()}
          scope="col"
        >
          <div className="date-header-inner-wrapper">
            <div className="day">{day}</div>
            <div className="day-of-week">{dayOfWeek}</div>
          </div>
        </th>
      );

      currentDateLuxon = currentDateLuxon.plus({ days: 1 });
    }
    return dateHeaderNodes;
  };

  const roomNodes: ReactNode[] = [];
  if (["ylangylangbeachresort"].includes(hotelShortName)) {
    units.forEach((unit) => {
      const roomIds = keys(
        dailyAvailabilitiesPerUnitId[unit.id].dailyAvailabilitiesByRoomId
      );

      roomIds.forEach((roomId) => {
        const availabilityCellNodes = [];

        let currentDateLuxon = availabilityCalendarStartDateLuxon;

        const dailyAvailabilities =
          dailyAvailabilitiesPerUnitId[unit.id].dailyAvailabilitiesByRoomId[
            roomId
          ];
        let dailyAvailabilityIndex = dailyAvailabilities.findIndex(
          (dailyAvailability) => dailyAvailability.date === visibleStartDate
        );
        while (currentDateLuxon <= availabilityCalendarEndDateLuxon) {
          const isDateAvailable = dailyAvailabilities[dailyAvailabilityIndex]
            ? dailyAvailabilities[dailyAvailabilityIndex].isAvailable
            : false;
          const availabilityClassName = isDateAvailable
            ? "available"
            : "not-available";
          const dayOfWeekClassName =
            WEEKDAY_TO_CLASSNAME[currentDateLuxon.weekday];

          availabilityCellNodes.push(
            <td
              className={`availability-cell ${availabilityClassName} ${dayOfWeekClassName}`}
              key={currentDateLuxon.toISODate()}
              data-date={currentDateLuxon.toISODate()}
            >
              <span className="availability-cell-content">
                {isDateAvailable ? "Is available" : "Not available"}
              </span>
            </td>
          );

          currentDateLuxon = currentDateLuxon.plus({ days: 1 });
          dailyAvailabilityIndex++;
        }

        roomNodes.push(
          <tr key={`${unit.id}-${roomId}`} data-roomId={roomId}>
            <td className="room-name">
              <span className="room-name-inner-wrapper" title={unit.name}>
                {unit.name}
              </span>
            </td>
            {availabilityCellNodes}
          </tr>
        );
      });
    });
  } else {
    units.forEach((unit) => {
      const availabilityCellNodes = [];
      let currentDateLuxon = availabilityCalendarStartDateLuxon;
      const dailyAvailabilities =
        dailyAvailabilitiesPerUnitId[unit.id].dailyAvailabilities;
      let dailyAvailabilityIndex = dailyAvailabilities.findIndex(
        (dailyAvailability) => dailyAvailability.date === visibleStartDate
      );
      while (currentDateLuxon <= availabilityCalendarEndDateLuxon) {
        const isDateAvailable = dailyAvailabilities[dailyAvailabilityIndex]
          ? dailyAvailabilities[dailyAvailabilityIndex].isAvailable
          : false;
        const availabilityClassName = isDateAvailable
          ? "available"
          : "not-available";
        const dayOfWeekClassName =
          WEEKDAY_TO_CLASSNAME[currentDateLuxon.weekday];

        availabilityCellNodes.push(
          <td
            className={`availability-cell ${availabilityClassName} ${dayOfWeekClassName}`}
            key={currentDateLuxon.toISODate()}
            data-date={currentDateLuxon.toISODate()}
          >
            <span className="availability-cell-content">
              {isDateAvailable ? "Is available" : "Not available"}
            </span>
          </td>
        );

        currentDateLuxon = currentDateLuxon.plus({ days: 1 });
        dailyAvailabilityIndex++;
      }

      roomNodes.push(
        <tr key={unit.id}>
          <td className="room-name">
            <span className="room-name-inner-wrapper" title={unit.name}>
              {unit.name}
            </span>
          </td>
          {availabilityCellNodes}
        </tr>
      );
    });
  }

  return (
    <div className="component hotel-availability-calendar">
      <div className="hotel-availability-calendar-container">
        <div className="table-window" ref={windowRef}>
          <table>
            <thead>
              <tr>
                <th className="room-name" rowSpan={2}>
                  <span className="room-name-inner-wrapper">
                    <span className="room-name-content">Room Name</span>
                  </span>
                </th>
                {renderMonthHeaderNodes()}
              </tr>
              <tr>{renderDateHeaderNodes()}</tr>
            </thead>
            <tbody>{roomNodes}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HotelAvailabilityCalendarClient;
